import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin } from "lucide-react";
import type { MscSite } from "@/app/booking-flow/publicBookingFlowTypes";

/**
 * Asks the applicant to accept a site that is not one of their preapproved ones, because it is the
 * only one with a time free. Shown by the ZIP step and by "Find another time", from useReserveForZip's
 * pendingSite; nothing is booked or carried forward until they say yes.
 */
export const AlternateSitePrompt = ({ site, onApprove, onDecline }: { site: MscSite; onApprove: () => void; onDecline: () => void }) => (
  <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
    <CardHeader className="text-center px-4">
      <CardTitle className="text-2xl font-semibold text-slate-900">Confirm Your Appointment Location</CardTitle>
      <CardDescription className="text-slate-500">
        Due to all available appointment slots being fully booked in your area at this time, the following site is the only available MSC office location for your appointment.
      </CardDescription>
    </CardHeader>

    <CardContent>
      <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <MapPin className="w-5 h-5 shrink-0 text-slate-500 mt-0.5" />
        <div>
          <p className="font-medium text-slate-900">{site.name}</p>
          <p className="text-sm text-slate-500">{site.address}</p>
          <p className="text-sm text-slate-500">{site.phone}</p>
        </div>
      </div>
    </CardContent>

    <CardFooter className="px-4 pb-6 flex flex-col gap-2 w-full max-w-md mx-auto">
      <Button className="w-full text-base h-12" onClick={onApprove}>
        Yes, book my appointment here
      </Button>
      <Button variant="outline" className="w-full text-base h-12" onClick={onDecline}>
        No, I can't make it to this location
      </Button>
    </CardFooter>
  </Card>
);
